import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const PENDING_WISH_KEY = "rfg_pending_wish";

const CustomerContext = createContext(null);

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => api.get("/account/session")
    .then((r) => setCustomer(r.data?.customer || null)).catch(() => setCustomer(null)).finally(() => setLoading(false)), []);
  useEffect(() => { refresh(); }, [refresh]);

  const customerId = customer?.id;
  useEffect(() => {
    if (!customerId) return;
    let pid = null;
    try { pid = sessionStorage.getItem(PENDING_WISH_KEY); sessionStorage.removeItem(PENDING_WISH_KEY); } catch { /* ignore */ }
    if (!pid) return;
    api.post(`/account/wishlist/${pid}`).then(({ data }) => {
      setCustomer((c) => (c ? { ...c, wishlist: data.ids } : c));
      toast.success("Saved to your wishlist");
    }).catch(() => {});
  }, [customerId]);

  async function call(path, body) {
    try {
      const { data } = await api.post(path, body);
      if (data.customer) setCustomer(data.customer);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  const login = (email, password) => call("/account/login", { email, password });
  const register = (name, email, password) => call("/account/register", { name, email, password });
  async function logout() {
    try { await api.post("/account/logout"); } catch { /* ignore */ }
    setCustomer(null);
  }

  const wishlist = customer?.wishlist || [];
  async function toggleWishlist(productId) {
    if (!customer) return { ok: false, needLogin: true };
    const on = wishlist.includes(productId);
    try {
      const { data } = on ? await api.delete(`/account/wishlist/${productId}`) : await api.post(`/account/wishlist/${productId}`);
      setCustomer((c) => (c ? { ...c, wishlist: data.ids } : c));
      return { ok: true, added: !on };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  return (
    <CustomerContext.Provider value={{ customer, setCustomer, loading, refresh, login, register, logout, call, wishlist, toggleWishlist }}>
      {children}
    </CustomerContext.Provider>
  );
}

export const useCustomer = () => useContext(CustomerContext);
